import React, { useRef, useCallback, useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import Flatpickr from 'react-flatpickr';
import flatpickr from 'flatpickr';
import flatpickrl10n from 'flatpickr/dist/l10n';

import { getOverrides, useClasses } from '../../../utils/overrides';
import Input from '../Input';
import Icon from '../../general/Icon';

import { createUseStyles } from '../../../utils/styles';
import styles from './styles';
const useStyles = createUseStyles(styles, 'DatePicker');

const TOKEN_LENGTH = {
    DD: 2,
    MM: 2,
    YYYY: 4,
};

const TOKEN_TO_FLATPICKR = {
    DD: 'd',
    MM: 'm',
    YYYY: 'Y',
};

const TODAY_BUTTON_ATTR = 'data-hoi-poi-datepicker-today';

const escapeRegex = (value = '') => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getMaskConfig = (maskFormat = '') => {
    const raw = maskFormat?.trim();
    const normalized = raw?.toUpperCase();
    if (!normalized) return null;

    const tokens = normalized.match(/DD|MM|YYYY/g);
    if (!tokens || tokens.length !== 3 || new Set(tokens).size !== 3) return null;

    const parts = normalized.split(/DD|MM|YYYY/);
    if (parts.length !== 4 || parts[0] !== '' || parts[3] !== '') return null;

    const separators = [parts[1], parts[2]];
    const flatpickrFormat = `${TOKEN_TO_FLATPICKR[tokens[0]]}${separators[0]}${TOKEN_TO_FLATPICKR[tokens[1]]}${separators[1]}${TOKEN_TO_FLATPICKR[tokens[2]]}`;

    return {
        mask: raw,
        tokens,
        separators,
        flatpickrFormat,
    };
};

const formatDateWithMask = (date, maskConfig) => {
    if (!date || !maskConfig) return '';
    const valuesByToken = {
        DD: String(date.getDate()).padStart(2, '0'),
        MM: String(date.getMonth() + 1).padStart(2, '0'),
        YYYY: String(date.getFullYear()),
    };

    return `${valuesByToken[maskConfig.tokens[0]]}${maskConfig.separators[0]}${valuesByToken[maskConfig.tokens[1]]}${maskConfig.separators[1]}${valuesByToken[maskConfig.tokens[2]]}`;
};

const maskDateInput = (rawValue = '', maskConfig) => {
    if (!maskConfig) return rawValue;
    const digits = rawValue.replace(/\D/g, '').slice(0, 8);
    let cursor = 0;
    let output = '';

    maskConfig.tokens.forEach((token, idx) => {
        const len = TOKEN_LENGTH[token];
        const chunk = digits.slice(cursor, cursor + len);
        if (!chunk) return;
        output += chunk;
        cursor += len;

        if (idx < maskConfig.separators.length && chunk.length === len && digits.length > cursor) {
            output += maskConfig.separators[idx];
        }
    });

    return output;
};

const parseMaskedDate = (maskedValue = '', maskConfig) => {
    if (!maskConfig) return null;
    const [token1, token2, token3] = maskConfig.tokens;
    const [separator1, separator2] = maskConfig.separators;
    const regex = new RegExp(
        `^(\\d{${TOKEN_LENGTH[token1]}})${escapeRegex(separator1)}(\\d{${TOKEN_LENGTH[token2]}})${escapeRegex(separator2)}(\\d{${TOKEN_LENGTH[token3]}})$`,
    );
    const match = maskedValue.match(regex);
    if (!match) return null;

    const byToken = {
        [token1]: Number(match[1]),
        [token2]: Number(match[2]),
        [token3]: Number(match[3]),
    };

    const day = byToken.DD;
    const month = byToken.MM;
    const year = byToken.YYYY;
    const date = new Date(year, month - 1, day);
    const isValid =
        date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
    return isValid ? date : null;
};

const clampDate = (date, minDate, maxDate) => {
    if (!date) return date;
    if (minDate && date.getTime() < minDate.getTime()) return minDate;
    if (maxDate && date.getTime() > maxDate.getTime()) return maxDate;
    return date;
};

// Old browser and Safari fix fix
(() => {
    if (document.getElementById('flatpickr')) return;
    const styleTag = document.createElement('style');
    styleTag.id = 'flatpickr';
    const headTag = document.getElementsByTagName('head')[0];
    headTag.insertBefore(styleTag, headTag.childNodes[0]);
})();

function DatePicker({
    children,
    classes: classesProp,
    overrides: overridesProp,
    className: classNameProp,
    name,
    formatDate,
    isFullWidth,
    minDate,
    maxDate,
    customComponent,
    labelMode = 'vertical',
    outputType = 'object',
    onChange = () => {},
    value = '',
    dateFormat = '',
    lang = 'en',
    isReadOnly = false,
    inputMaskFormat,
    calendarButtonLabel = 'Today',
    placeholder = 'Select date',
    placeholderReadOnly,
    onBlur: onBlurProp,
    ...inputProps
}) {
    const flatpickrRef = useRef(null);
    const todayButtonRef = useRef();
    const hasManualInputRef = useRef(false);
    const [typingValue, setTypingValue] = useState('');
    const classes = useClasses(useStyles, classesProp);

    // Overrides
    const override = getOverrides(overridesProp, DatePicker.overrides);

    // Classes
    const rootClassName = classnames(
        classes.root,
        {
            [classes.isFullWidth]: isFullWidth,
        },
        classNameProp,
    );

    const maskConfig = useMemo(() => getMaskConfig(inputMaskFormat), [inputMaskFormat]);
    const isMaskedTypingEnabled = !isReadOnly && !!maskConfig;
    const flatpickrDateFormat = isMaskedTypingEnabled
        ? maskConfig.flatpickrFormat
        : dateFormat || 'd M Y';
    const effectivePlaceholder = isMaskedTypingEnabled ? maskConfig.mask : placeholder;

    const flatpickrOptions = useMemo(() => {
        return {
            dateFormat: flatpickrDateFormat,
            formatDate,
            locale: flatpickrl10n[lang],
            clickOpens: !isReadOnly,
            time_24hr: true,
            minDate,
            maxDate,
            disableMobile: true,
            allowInput: isMaskedTypingEnabled,
            ...override.flatpickrOptions,
        };
    }, [
        flatpickrDateFormat,
        formatDate,
        isMaskedTypingEnabled,
        isReadOnly,
        lang,
        maxDate,
        minDate,
        override.flatpickrOptions,
    ]);

    const onChangeDate = useCallback(
        (date) => {
            if (isReadOnly) return;
            if (!date?.[0]) return;
            const formattedDate =
                outputType === 'string'
                    ? flatpickr.formatDate(date[0], flatpickrOptions.dateFormat)
                    : date[0];
            hasManualInputRef.current = false;
            if (isMaskedTypingEnabled) {
                setTypingValue(formatDateWithMask(date[0], maskConfig));
            }
            onChange && onChange(formattedDate, name);
        },
        [
            flatpickrOptions.dateFormat,
            isReadOnly,
            isMaskedTypingEnabled,
            maskConfig,
            name,
            onChange,
            outputType,
        ],
    );

    useEffect(() => {
        if (!isMaskedTypingEnabled) return;
        hasManualInputRef.current = false;
        if (!value) {
            setTypingValue('');
            return;
        }

        let dateToFormat = null;
        if (value instanceof Date) dateToFormat = value;
        else if (typeof value === 'string') {
            dateToFormat =
                flatpickr.parseDate(value, flatpickrDateFormat) ||
                flatpickr.parseDate(value, 'Y-m-d') ||
                parseMaskedDate(value, maskConfig);
        }

        if (dateToFormat) setTypingValue(formatDateWithMask(dateToFormat, maskConfig));
        else setTypingValue(maskDateInput(String(value), maskConfig));
    }, [isMaskedTypingEnabled, flatpickrDateFormat, maskConfig, value]);

    const onInputChange = useCallback(
        (finalValue, e, opts) => {
            if (isReadOnly) return;
            if (opts?.action === 'clear') {
                flatpickrRef.current?.clear();
                hasManualInputRef.current = false;
                setTypingValue('');
                onChange && onChange(undefined, name);
            } else if (isMaskedTypingEnabled) {
                hasManualInputRef.current = true;
                setTypingValue(maskDateInput(finalValue, maskConfig));
            } else {
                onChange && onChange(undefined, name);
            }
        },
        [isReadOnly, isMaskedTypingEnabled, maskConfig, name, onChange],
    );

    const onInputBlur = useCallback(
        (rawValue, e) => {
            if (!isMaskedTypingEnabled || isReadOnly) {
                onBlurProp && onBlurProp(rawValue, e);
                return;
            }

            const calendarContainer = flatpickrRef.current?.calendarContainer;
            const blurTargetInCalendar =
                calendarContainer && e?.relatedTarget
                    ? calendarContainer.contains(e.relatedTarget)
                    : false;
            const isCalendarOpen = !!flatpickrRef.current?.isOpen;

            // Ignore blur caused by calendar interaction, and skip normalization when user didn't type.
            if (blurTargetInCalendar || isCalendarOpen || !hasManualInputRef.current) {
                onBlurProp && onBlurProp(rawValue, e);
                return;
            }

            const maskedValue = maskDateInput(rawValue, maskConfig);
            const today = new Date(new Date().setHours(0, 0, 0, 0));
            const parsedDate = parseMaskedDate(maskedValue, maskConfig);
            const finalDate = clampDate(parsedDate || today, minDate, maxDate);
            const normalizedMask = formatDateWithMask(finalDate, maskConfig);
            hasManualInputRef.current = false;
            setTypingValue(normalizedMask);
            flatpickrRef.current?.setDate(finalDate, false);

            const outValue =
                outputType === 'string'
                    ? flatpickr.formatDate(finalDate, flatpickrOptions.dateFormat)
                    : finalDate;
            onChange && onChange(outValue, name);
            onBlurProp && onBlurProp(normalizedMask, e);
        },
        [
            isMaskedTypingEnabled,
            isReadOnly,
            onBlurProp,
            minDate,
            maskConfig,
            maxDate,
            outputType,
            flatpickrOptions.dateFormat,
            onChange,
            name,
        ],
    );

    const shouldDisableToday = useMemo(() => {
        if (minDate || maxDate) {
            const today = new Date();
            if (minDate && minDate.getTime() > today.getTime()) return true;
            if (maxDate && maxDate.getTime() < today.getTime()) return true;
        }
        return false;
    }, [minDate, maxDate]);

    const todayClicked = useCallback(() => {
        if (shouldDisableToday) return;
        const today = new Date(new Date().setHours(0, 0, 0, 0));
        onChangeDate([today]);
        flatpickrRef.current?.close();
    }, [onChangeDate, shouldDisableToday]);

    const onReady = useCallback(
        (_, __, fp) => {
            flatpickrRef.current = fp;
            fp.calendarContainer.classList.add(classes.container);
            if (fp.rContainer) {
                const existingButton = fp.rContainer.querySelector(`[${TODAY_BUTTON_ATTR}]`);
                if (existingButton) {
                    if (todayButtonRef.current?.element === existingButton) {
                        existingButton.removeEventListener(
                            'click',
                            todayButtonRef.current.function,
                        );
                    }
                    existingButton.remove();
                }
                const div = document.createElement('div');
                const classNames = [classes.todayContainer];
                if (shouldDisableToday) classNames.push(classes.todayContainerDisabled);
                div.className = classNames.join(' ');
                div.setAttribute(TODAY_BUTTON_ATTR, 'true');
                div.addEventListener('click', todayClicked);
                div.textContent = calendarButtonLabel;
                todayButtonRef.current = {
                    element: div,
                    function: todayClicked,
                };
                fp.rContainer.appendChild(div);
            }
        },
        [
            classes.container,
            classes.todayContainer,
            classes.todayContainerDisabled,
            shouldDisableToday,
            todayClicked,
            calendarButtonLabel,
        ],
    );

    useEffect(() => {
        if (todayButtonRef.current) {
            todayButtonRef.current.element.removeEventListener(
                'click',
                todayButtonRef.current.function,
            );
            todayButtonRef.current.element.addEventListener('click', todayClicked);
            todayButtonRef.current.function = todayClicked;
        }
    }, [classes.todayContainer, classes.todayContainerDisabled, todayClicked]);

    useEffect(() => {
        if (todayButtonRef.current) {
            const classNames = [classes.todayContainer];
            if (shouldDisableToday) classNames.push(classes.todayContainerDisabled);
            todayButtonRef.current.element.className = classNames.join(' ');
        }
    }, [classes.todayContainer, classes.todayContainerDisabled, shouldDisableToday]);

    useEffect(() => {
        return () => {
            if (todayButtonRef.current?.element) {
                todayButtonRef.current.element.removeEventListener(
                    'click',
                    todayButtonRef.current.function,
                );
                todayButtonRef.current.element.remove();
                todayButtonRef.current = undefined;
            }
        };
    }, []);

    const onClick = useCallback((e) => {
        setTimeout(() => {
            try {
                flatpickrRef.current?.open();
            } catch {
                // flatpickr may be destroyed before deferred open runs
            }
        });
    }, []);

    const flatpickrRender = useCallback(
        ({ className, value }, ref) => {
            const formatValue =
                value && outputType === 'object'
                    ? formatDate
                        ? formatDate(value, flatpickrOptions.dateFormat)
                        : flatpickr.formatDate(value, flatpickrOptions.dateFormat)
                    : value;
            if (customComponent) {
                const CustomComponent = customComponent;
                return (
                    <CustomComponent
                        {...inputProps}
                        inputRef={ref}
                        value={formatValue}
                        className={className}
                        onClick={onClick}
                    />
                );
            }
            return (
                <Input
                    {...inputProps}
                    type="text"
                    labelMode={labelMode}
                    value={isMaskedTypingEnabled ? typingValue : formatValue}
                    className={className}
                    isReadOnly={isReadOnly}
                    isFullWidth={isFullWidth}
                    onChange={onInputChange}
                    onBlur={onInputBlur}
                    overrides={{ input: { ref } }}
                    placeholder={effectivePlaceholder}
                    placeholderReadOnly={placeholderReadOnly}
                    postComponent={
                        !isReadOnly && (
                            <div className={classes.calendarIcon} onClick={onClick}>
                                <Icon name="calendar" color="currentColor" />
                            </div>
                        )
                    }
                />
            );
        },
        [
            classes.calendarIcon,
            isMaskedTypingEnabled,
            effectivePlaceholder,
            flatpickrOptions.dateFormat,
            formatDate,
            isFullWidth,
            isReadOnly,
            labelMode,
            typingValue,
            onClick,
            onInputBlur,
            onInputChange,
            outputType,
            placeholderReadOnly,
            inputProps,
            customComponent,
        ],
    );

    const key = `flatpickr-${name || 'anon'}--${isReadOnly ? 'read-only' : 'active'}`;

    return (
        <Flatpickr
            className={rootClassName}
            key={key}
            options={flatpickrOptions}
            render={flatpickrRender}
            overrides={overridesProp}
            value={value}
            onReady={onReady}
            onChange={onChangeDate}
            {...override.flatpickr}
        />
    );
}

DatePicker.overrides = ['root', 'input', 'flatpickr', 'flatpickrOptions'];

DatePicker.propTypes = {
    /** Custom className for styling purposes */
    className: PropTypes.string,
    /** Object with custom style overrides for inner elements */
    overrides: PropTypes.object,
    /** Function called when date selection changes with selected date as parameter */
    onChange: PropTypes.func,
    /** Function called when the input receives focus */
    onFocus: PropTypes.func,
    /** Function called when the input loses focus */
    onBlur: PropTypes.func,
    /** Function called when Enter key is pressed while input is focused */
    onEnter: PropTypes.func,
    /** HTML id attribute assigned to the input element */
    id: PropTypes.string,
    /** HTML name attribute assigned to the input element */
    name: PropTypes.string,
    /** Current selected date (can be Date object, ISO string, or formatted string) */
    value: PropTypes.any,
    /** Text to be displayed as the input's label */
    label: PropTypes.string,
    /** Determines how the label is positioned relative to the input */
    labelMode: PropTypes.oneOf(['horizontal', 'vertical']),
    /** When true, component will take up 100% of the available width */
    isFullWidth: PropTypes.bool,
    /** Tooltip text displayed in an info icon next to the label */
    hint: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    /** Error message displayed below the component, also triggers error styling when present */
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    /** Informational message displayed below the component */
    info: PropTypes.string,
    /** When true, displays an asterisk next to the label indicating the field is required */
    isRequired: PropTypes.bool,
    /** When true, prevents user from changing the input value */
    isReadOnly: PropTypes.bool,
    /** Enables masked typing mode with dynamic masks like DD/MM/YYYY, MM/DD/YYYY, DD-MM-YYYY (tokens are case-insensitive) */
    inputMaskFormat: PropTypes.string,
    /** Determines the format of the onChange callback value ('object' returns Date object, 'string' returns formatted string) */
    outputType: PropTypes.oneOf(['object', 'string']),
    /** Text displayed when no date is selected */
    placeholder: PropTypes.string,
    /** Text displayed when no date is selected and in read-only mode */
    placeholderReadOnly: PropTypes.string,
    /** Language code used for localization (e.g., 'en', 'es', 'fr') */
    lang: PropTypes.string,
    /** Date format string using flatpickr's tokens (e.g., 'Y-m-d' for YYYY-MM-DD) */
    dateFormat: PropTypes.string,
    /** Custom function to format displayed date (receives Date object and format string) */
    formatDate: PropTypes.func,
    /** Custom text for the "Today" button in calendar */
    calendarButtonLabel: PropTypes.string,
    /** Earliest selectable date */
    minDate: PropTypes.instanceOf(Date),
    /** Latest selectable date */
    maxDate: PropTypes.instanceOf(Date),
    /** Custom component to replace the default input element */
    customComponent: PropTypes.elementType,
};

export default React.memo(DatePicker);
